// Small helpers shared by the parts of Today.
import { useEffect, useRef, type RefObject } from 'react';
import type { ActivityType, Reminder, TodayItem } from '@dpw/shared';
import { addDays, localDay, localTime, gbDate } from '../../lib/format';

export const TYPE_WORD: Record<ActivityType, string> = { meeting: 'Meeting', workshop: 'Workshop', deliverable: 'Deliverable', task: 'Task' };
export const TYPE_CLS: Record<ActivityType, string> = { meeting: 'meet', workshop: 'work', deliverable: 'deliv', task: 'task' };
export const TYPE_COLOR: Record<ActivityType, string> = { meeting: 'var(--t-meet)', workshop: 'var(--t-work)', deliverable: 'var(--t-deliv)', task: 'var(--t-task)' };
export const isMeeting = (t: ActivityType) => t === 'meeting' || t === 'workshop';

/** "08:30" to 8.5. */
export const hourOf = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return (h ?? 0) + (m ?? 0) / 60; };
/** "in 1h 20m", "in 15m", "now". */
export function inTime(hhmm: string, now: Date) {
  const mins = Math.round((hourOf(hhmm) - hourOf(localTime(now.toISOString()))) * 60);
  if (mins <= 0) return 'now';
  const h = Math.floor(mins / 60), m = mins % 60;
  return `in ${h ? `${h}h` : ''}${h && m ? ' ' : ''}${m ? `${m}m` : ''}`;
}
export const longDay = (d: string) => gbDate(new Date(`${d}T12:00:00Z`), { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' });
export const weekdayOf = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
export const dayNum = (d: string) => Number(d.slice(8, 10));
export const monthOf = (d: string) => gbDate(new Date(`${d}T12:00:00Z`), { month: 'short', timeZone: 'UTC' });

/**
 * Meeting blocks on the schedule strip, in pixels: each at its time and at least min wide, never over another one and
 * never past either end of the strip. A block crowded by its neighbour or by the end of the day moves just enough
 * (its label still says its time); when too many share too little room they share it evenly.
 */
export function placeBlocks(items: { id: string; at: number; w: number }[], width: number, min: number, gap = 4): Map<string, { left: number; w: number }> {
  const bs = items.map(x => ({ id: x.id, want: x.at, w: Math.max(min, x.w), left: 0 })).sort((p, q) => p.want - q.want);
  let edge = 0;
  for (const b of bs) { b.left = Math.max(b.want, edge); edge = b.left + b.w + gap; }
  let bound = width;
  for (const b of [...bs].reverse()) { b.left = Math.min(b.left, bound - b.w); bound = b.left - gap; }
  if (bs.length && bs[0]!.left < 0) {
    const each = (width - gap * (bs.length - 1)) / bs.length;
    bs.forEach((b, i) => { b.w = each; b.left = i * (each + gap); });
  }
  return new Map(bs.map(b => [b.id, { left: b.left, w: b.w }]));
}

/** The day a reminder is for, local. */
export const remDay = (r: Reminder) => (r.remindAt ? localDay(r.remindAt) : r.remindDate!);
/** Reminders for the Today or Tomorrow tab: today also has the overdue ones; done ones stay (ticked) for the day. */
export function remindersFor(list: Reminder[], day: 'today' | 'tomorrow', today: string) {
  const tomorrow = addDays(today, 1);
  const rank = (r: Reminder) => (r.state === 'overdue' ? '0' : r.state === 'late' ? '1' : r.remindAt ? `2${localTime(r.remindAt)}` : '3');
  return list.filter(r => (day === 'today' ? (r.state === 'overdue' || remDay(r) === today) : remDay(r) === tomorrow))
    .filter(r => r.state !== 'done' || (r.doneAt && localDay(r.doneAt) === today))
    .sort((x, y) => rank(x).localeCompare(rank(y)) || x.text.localeCompare(y.text));
}

/** A due item's progress line: "80% · On track". */
export const progressText = (x: TodayItem) => (isMeeting(x.type)
  ? (x.stepsTotal ? `Prep ${x.stepsDone} of ${x.stepsTotal} done` : 'No prep')
  : `${x.progress}% · ${x.state.label}`);
export const progressColor = (x: TodayItem) => (x.state.bad ? 'var(--red)' : x.progress >= 100 ? 'var(--grn)' : x.progress > 0 ? 'var(--amb)' : 'var(--line2)');

/**
 * "N more below" on a scroll area: counts the rows (data-row) whose lower part is below the fold and writes it to
 * data-more (shown on a faded band by CSS), updated on scroll, resize and when rows change.
 */
export function useMoreBelow(ref: RefObject<HTMLElement | null>) {
  const raf = useRef(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const up = () => {
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => {
        const end = el.scrollTop + el.clientHeight;
        let n = 0;
        for (const r of el.querySelectorAll<HTMLElement>('[data-row]')) if (r.offsetTop + r.offsetHeight * 0.6 > end) n++;
        el.toggleAttribute('data-has-more', n > 0);
        el.setAttribute('data-more', `${n} more below`);
      });
    };
    up();
    el.addEventListener('scroll', up, { passive: true });
    const ro = new ResizeObserver(up);
    ro.observe(el);
    const mo = new MutationObserver(up);
    mo.observe(el, { childList: true, subtree: true });
    return () => { el.removeEventListener('scroll', up); ro.disconnect(); mo.disconnect(); cancelAnimationFrame(raf.current); };
  }, [ref]);
}

/**
 * When "I will call Faisal" sets its reminder (team time): the next full hour from 08:00 to 19:59; from 20:00 on,
 * tomorrow at 09:00 (never a time past midnight that still says "today"); before 08:00, today at 09:00.
 * label is what the row says ("today 15:00", "tomorrow 09:00").
 */
export function callTime(now: Date): { day: string; at: string; label: string; tomorrow: boolean } {
  const iso = now.toISOString();
  const today = localDay(iso);
  const h = Number(localTime(iso).slice(0, 2));
  if (h >= 20) return { day: addDays(today, 1), at: '09:00', label: 'tomorrow 09:00', tomorrow: true };
  const at = h < 8 ? '09:00' : `${String(h + 1).padStart(2, '0')}:00`;
  return { day: today, at, label: `today ${at}`, tomorrow: false };
}

/**
 * The hours the schedule strip spans: 08:00 to 18:00, widened to what is on today (hs, hours as numbers) and to
 * now while now is inside 06:00 to 22:00. At night (before 06:00, after 22:00) the strip stays on the working day and
 * the Now line is left off unless now happens to fall inside it. untimed: room at the end for the "No set time" marker.
 */
export function stripHours(hs: number[], nowH: number, untimed: boolean): { from: number; to: number; nowIn: boolean } {
  const from = Math.max(0, Math.min(8, ...hs.map(Math.floor), nowH >= 6 ? Math.floor(nowH) : 8));
  const to = Math.min(24, Math.max(18, ...hs.map(h => Math.ceil(h + 0.5)), nowH <= 22 ? Math.ceil(nowH + 0.5) : 18) + (untimed ? 0.6 : 0));
  return { from, to, nowIn: nowH >= from && nowH <= to };
}
